import React from 'react';
import { ViewMode, ConversationSummary } from '../../domain/types';
import { SIDEBAR_COLLAPSED_PX, SIDEBAR_EXPANDED_PX } from './sidebarConstants';
import { SidebarHeader } from './SidebarHeader';
import { SidebarNavSections } from './SidebarNavSections';
import { SidebarToolsSection } from './SidebarToolsSection';
import { SidebarConversationsSection } from './SidebarConversationsSection';
import { SidebarFooter } from './SidebarFooter';
import type { WorkspaceToolType } from '../modals/ToolDrawerModal';
import type { ReleaseUpdateState } from '../../hooks/useReleaseUpdate';
import {
  buildSidebarNavSources,
  resolveAuthLabel,
  resolveAvatarInitials,
  resolveShowSidebarLabels,
  resolveSidebarWidthPx,
  sidebarShellStyle,
  type SidebarCounts,
} from './sidebarModel';

export { SIDEBAR_EXPANDED_PX, SIDEBAR_COLLAPSED_PX };

export interface AppSidebarProps {
  isCompact: boolean;
  isSidebarExpanded: boolean;
  isDrawerOpen: boolean;
  isSettingsOpen: boolean;
  sidebarRef: React.RefObject<HTMLElement | null>;
  view: ViewMode;
  counts: SidebarCounts;
  loadingSources: Partial<Record<'jira' | 'slack' | 'github', boolean>>;
  dailySent: boolean;
  activeTool: WorkspaceToolType;
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  authUser?: {
    photoURL?: string | null;
    email?: string | null;
    displayName?: string | null;
  } | null;
  onToggleSidebar: () => void;
  onNavigateToView: (view: ViewMode) => void;
  onOpenTool: (tool: Exclude<WorkspaceToolType, null>) => void;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onOpenSettings: () => void;
  onOpenSearch: () => void;
  onCloseDrawer: () => void;
  releaseUpdate?: ReleaseUpdateState;
}

function sidebarInertProps(
  isCompact: boolean,
  isDrawerOpen: boolean,
): React.HTMLAttributes<HTMLElement> {
  if (isCompact && !isDrawerOpen) {
    return { inert: '' } as React.HTMLAttributes<HTMLElement>;
  }
  return {};
}

export const AppSidebar: React.FC<AppSidebarProps> = (props) => {
  const showSidebarLabels = resolveShowSidebarLabels(props.isCompact, props.isSidebarExpanded);
  const sidebarWidthPx = resolveSidebarWidthPx(props.isCompact, props.isSidebarExpanded);
  const navSources = buildSidebarNavSources(props.counts, props.loadingSources);
  const authLabel = resolveAuthLabel(props.authUser?.displayName, props.authUser?.email);
  const avatarInitials = resolveAvatarInitials(props.authUser?.email);

  return (
    <aside
      id="app-sidebar"
      className="z-sidebar"
      data-testid="app-sidebar"
      data-expanded={showSidebarLabels ? 'true' : 'false'}
      data-drawer-open={props.isCompact ? (props.isDrawerOpen ? 'true' : 'false') : undefined}
      aria-label="Main navigation"
      aria-hidden={props.isCompact && !props.isDrawerOpen ? true : undefined}
      {...sidebarInertProps(props.isCompact, props.isDrawerOpen)}
      style={sidebarShellStyle(sidebarWidthPx, showSidebarLabels, props.isCompact)}
      ref={props.sidebarRef as React.RefObject<HTMLDivElement>}
    >
      <SidebarHeader
        showSidebarLabels={showSidebarLabels}
        isCompact={props.isCompact}
        isSidebarExpanded={props.isSidebarExpanded}
        onNavigateToRadar={() => props.onNavigateToView('radar')}
        onToggleSidebar={props.onToggleSidebar}
      />
      <SidebarNavSections
        showSidebarLabels={showSidebarLabels}
        view={props.view}
        radarCount={props.counts.radar}
        notificationsCount={props.counts.notifications}
        navSources={navSources}
        onNavigateToView={props.onNavigateToView}
      />
      <SidebarToolsSection
        showSidebarLabels={showSidebarLabels}
        dailySent={props.dailySent}
        activeTool={props.activeTool}
        remindersCount={props.counts.reminders}
        notesCount={props.counts.notes}
        onOpenTool={props.onOpenTool}
      />
      <SidebarConversationsSection
        showSidebarLabels={showSidebarLabels}
        conversations={props.conversations}
        activeConversationId={props.activeConversationId}
        onNewChat={props.onNewChat}
        onSelectConversation={props.onSelectConversation}
        onDeleteConversation={props.onDeleteConversation}
      />
      <SidebarFooter
        showSidebarLabels={showSidebarLabels}
        isSettingsOpen={props.isSettingsOpen}
        authLabel={authLabel}
        avatarInitials={avatarInitials}
        photoURL={props.authUser?.photoURL}
        onOpenSettings={props.onOpenSettings}
        onOpenSearch={props.onOpenSearch}
        onCloseDrawer={props.onCloseDrawer}
        releaseUpdate={props.releaseUpdate}
      />
    </aside>
  );
};
